import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import FlightRoundedIcon from '@mui/icons-material/FlightRounded'
import CheckroomRoundedIcon from '@mui/icons-material/CheckroomRounded'
import EmailRoundedIcon from '@mui/icons-material/EmailRounded'
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded'
import type { EventInfo } from '../data/types'
import {
  ALL_EVENT_INFO_SECTIONS,
  type EventInfoSections,
} from './eventInfo'
import { mapEmbedUrl } from '../utils/mapEmbed'

/**
 * Renders an event's logistics ("Welcome and Logistics" fields from the admin
 * Edit Event form) — welcome message, venue + map, travel, dress code, FAQs, and
 * key contacts. Shared by the standalone Event Info page and the "Event Info"
 * page-builder block, so both stay consistent. Each sub-section renders only
 * when enabled AND the event actually has that data.
 */
export default function EventInfoSection({
  event,
  sections = ALL_EVENT_INFO_SECTIONS,
}: {
  event: EventInfo
  sections?: EventInfoSections
}) {
  const venue = event.venue
  const hasVenue = Boolean(venue && (venue.name || venue.address || venue.city))
  const embedMap = sections.venue ? mapEmbedUrl(venue?.mapUrl) : null
  const logisticsRows: React.ReactNode[] = []

  if (sections.venue && hasVenue) {
    logisticsRows.push(
      <InfoRow
        key="venue"
        icon={<PlaceRoundedIcon color="primary" />}
        title={venue.name || 'Venue'}
        body={[venue.address, venue.city].filter(Boolean).join(', ')}
        action={
          venue.mapUrl && /^https?:\/\//i.test(venue.mapUrl) ? (
            <Button size="small" href={venue.mapUrl} target="_blank" rel="noopener">
              {embedMap ? 'Open in Maps' : 'Map'}
            </Button>
          ) : undefined
        }
      />,
    )
  }
  if (sections.travel && event.travel) {
    logisticsRows.push(
      <InfoRow
        key="travel"
        icon={<FlightRoundedIcon color="primary" />}
        title="Travel & parking"
        body={event.travel}
      />,
    )
  }
  if (sections.dressCode && event.dressCode) {
    logisticsRows.push(
      <InfoRow
        key="dress"
        icon={<CheckroomRoundedIcon color="primary" />}
        title="Dress code"
        body={event.dressCode}
      />,
    )
  }

  const showWelcome = Boolean(sections.welcome && event.welcomeMessage)
  const showFaqs = Boolean(sections.faqs && event.faqs?.length)
  const showContacts = Boolean(sections.contacts && event.contacts?.length)

  return (
    <Stack spacing={3}>
      {showWelcome && (
        <Card variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {event.welcomeMessage}
          </Typography>
        </Card>
      )}

      {logisticsRows.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Stack divider={<Divider />}>{logisticsRows}</Stack>
          {embedMap && (
            <Box
              component="iframe"
              src={embedMap}
              title="Venue map"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              sx={{
                width: '100%',
                height: 280,
                border: 0,
                display: 'block',
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            />
          )}
        </Card>
      )}

      {showFaqs && (
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            FAQs
          </Typography>
          {event.faqs.map((f, i) => (
            <Accordion key={i} disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                <Typography fontWeight={600}>{f.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {f.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {showContacts && (
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Key contacts
          </Typography>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <Stack divider={<Divider />}>
              {event.contacts.map((c, i) => (
                <Box key={i} sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {c.name}
                    {c.role ? ` · ${c.role}` : ''}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    {c.email && (
                      <Button
                        size="small"
                        startIcon={<EmailRoundedIcon fontSize="small" />}
                        href={`mailto:${c.email}`}
                      >
                        {c.email}
                      </Button>
                    )}
                    {c.phone && (
                      <Button
                        size="small"
                        startIcon={<PhoneRoundedIcon fontSize="small" />}
                        href={`tel:${c.phone}`}
                      >
                        {c.phone}
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Card>
        </Box>
      )}
    </Stack>
  )
}

function InfoRow({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode
  title: string
  body?: string
  action?: React.ReactNode
}) {
  if (!body) return null
  return (
    <Box sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ mt: 0.25 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
          {body}
        </Typography>
      </Box>
      {action}
    </Box>
  )
}
